import { arrayMoveImmutable } from 'array-move';
import {
  SortableContainer,
  SortableContainerProps,
  SortableElement,
  SortableElementProps,
  SortEnd,
} from 'react-sortable-hoc';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTheme } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import { UploadBox } from 'src/components/upload';
import { uploadImagesAccept } from 'src/components/upload/helpers';
import { IconButton, styled } from '@mui/material';
import Iconify from 'src/components/iconify';
import { isArray, isString } from 'lodash';

// Element
interface SortableItemProps extends SortableElementProps {
  isCover: boolean;
  url: string;
  onCopy?: (url: string) => void;
  onDelete: (url: string) => void;
}

const SortableItemStyledBox = styled(Box)({
  position: 'relative',
  '&:hover': {
    '& .image': {
      opacity: 0.25,
    },
    '& .button': {
      opacity: 1,
    },
  },
});

const SortableItem = SortableElement<SortableItemProps>(
  ({ isCover, url, onDelete, onCopy }: SortableItemProps) => {
    const theme = useTheme();

    // Open image in new tab
    const handleFullScreen = () => {
      window.open(url, '_blank');
    };

    const handleDelete = () => {
      onDelete(url);
    };

    const handleCopy = () => {
      onCopy?.(url);
    };

    return (
      <SortableItemStyledBox
        sx={{
          borderRadius: 1,
          background: theme.palette.background.default,
          boxShadow: isCover ? `0 0 0 2px ${theme.palette.error.main}` : '',
          overflow: 'hidden',
          userSelect: 'none',
        }}
      >
        <Box
          component="img"
          className="image"
          src={url}
          alt={url}
          sx={{
            objectFit: 'cover',
            objectPosition: 'center',
            width: '100px',
            pointerEvents: 'none',
            aspectRatio: '4/3',
          }}
        />
        {isCover && (
          <Chip
            sx={{
              pointerEvents: 'none',
              position: 'absolute',
              top: 4,
              left: 4,
            }}
            label="Cover"
            variant="filled"
            color="error"
            size="small"
          />
        )}

        <IconButton
          className="button"
          color="error"
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            zIndex: 2,
            opacity: 0,
          }}
          size="small"
          onClick={handleDelete}
        >
          <Iconify icon="mdi:trash" />
        </IconButton>

        <IconButton
          className="button"
          color="error"
          sx={{ position: 'absolute', top: 0, right: 0, zIndex: 2, opacity: 0 }}
          size="small"
          onClick={handleFullScreen}
        >
          <Iconify icon="mdi:fullscreen" />
        </IconButton>

        {!!onCopy && (
          <IconButton
            className="button"
            color="error"
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              zIndex: 2,
              opacity: 0,
            }}
            size="small"
            onClick={handleCopy}
          >
            <Iconify icon="mdi:arrow-bottom-bold" />
          </IconButton>
        )}
        {/* <IconButton
          className="more"
          sx={{ position: 'absolute', top: 0, right: 0, zIndex: 2, opacity: 0 }}
          aria-label="more"
          aria-controls="long-menu"
          aria-haspopup="true"
          size="small"
          onClick={handleOpen}
        >
          <Iconify icon="eva:more-vertical-fill" />
        </IconButton> */}

        {/* <Menu
          anchorEl={() => elementRef.current!}
          open={open}
          onClose={handleClose}
        >
          <MenuItem onClick={handleFullScreen}>Full screen</MenuItem>
          <MenuItem onClick={handleDelete}>
            <Typography variant="inherit" color={theme.palette.error.main}>
              Delete
            </Typography>
          </MenuItem>
        </Menu> */}
      </SortableItemStyledBox>
    );
  }
);

// Container
interface SortableListProps extends SortableContainerProps {
  photos: string[];
  onCopy?: (url: string) => void;
  onDelete: (url: string) => void;
  onUploadDrop: (files: File[]) => void;
}

const SortableList = SortableContainer<SortableListProps>(
  ({ photos, onUploadDrop, ...restProps }: SortableListProps) => (
    <Stack direction="row" flexWrap="wrap" gap={1.5}>
      <UploadBox
        icon="solar:camera-add-bold"
        sx={{ width: 100, height: 75, m: 0 }}
        onDrop={onUploadDrop}
        accept={uploadImagesAccept}
      />

      {photos.map((value, index) => (
        <SortableItem
          key={index}
          index={index}
          url={value}
          isCover={index === 0}
          {...restProps}
        />
      ))}
    </Stack>
  )
);

export type RHFImagesFieldProps = {
  name: string;
  uploadName: string;

  copyName?: string;
  copyUploadName?: string;
};

export function RHFImagesField({
  name,
  uploadName,
  copyName,
  copyUploadName,
}: RHFImagesFieldProps) {
  // Get form context
  const form = useFormContext();

  const pictures: string[] = useWatch({ name, defaultValue: [] });

  const handleSortEnd = ({ oldIndex, newIndex }: SortEnd) => {
    if (oldIndex !== newIndex) {
      const items: string[] = form.getValues(name);
      form.setValue(name, arrayMoveImmutable(items, oldIndex, newIndex));
    }
  };

  const handleCopy = (url: string) => {
    if (!copyName || !copyUploadName) {
      return;
    }

    const isBlob = url.startsWith('blob:');
    const blobItems = form.getValues(uploadName) ?? {};
    const copyItems = form.getValues(copyName) ?? [];
    const copyBlobItems = form.getValues(copyUploadName) ?? {};
    const value = (isArray(copyItems) ? copyItems : [copyItems]).filter(
      Boolean
    ) as string[];

    form.setValue(copyName, [...value, url]);

    // Copy blob items
    if (isBlob) {
      form.setValue(copyUploadName, {
        ...copyBlobItems,
        [url]: blobItems[url],
      });
    }
  };

  const handleDelete = (url: string) => {
    const items = form.getValues(name);
    const blobItems = form.getValues(uploadName) ?? {};
    const value = (isArray(items) ? items : [items]).filter(
      Boolean
    ) as string[];

    if (!value.length) {
      return;
    }

    form.setValue(
      name,
      value.filter((picture) => picture !== url)
    );

    form.setValue(
      uploadName,
      Object.keys(blobItems).reduce((acc, key) => {
        if (key !== url) {
          acc[key] = blobItems[key];
        }

        return acc;
      }, {} as any)
    );
  };

  const handleUploadDrop = (files: File[]) => {
    const localUrls = files.reduce(
      (acc, file) => {
        acc[URL.createObjectURL(file)] = file;

        return acc;
      },
      {} as Record<string, File>
    );
    const items: string[] = form.getValues(name) ?? [];
    const blobItems: Record<string, File> = form.getValues(uploadName) ?? {};

    form.setValue(name, [...items, ...Object.keys(localUrls)]);
    form.setValue(uploadName, { ...blobItems, ...localUrls });
  };

  return (
    <SortableList
      axis="xy"
      pressDelay={100}
      photos={isString(pictures) ? [] : pictures ?? []}
      onSortEnd={handleSortEnd}
      onDelete={handleDelete}
      onCopy={copyName ? handleCopy : undefined}
      onUploadDrop={handleUploadDrop}
    />
  );
}
